import React, { useState, useEffect } from "react";
import {
  Button,
  TextInput,
  Card,
  Flex,
  Select,
  SelectItem,
  Dialog,
  DialogPanel,
  Title,
  Text,
} from "@tremor/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { profileSchema } from "./schema";
import * as profilesAPI from "../../services/profiles";
import * as sourceAPI from "../../services/sources";
import { useNavigate } from "react-router-dom";
import IconPicker from "../../components/IconPicker";

const textStyle =
  "mt-2 text-tremor-default leading-6 text-tremor-content dark:text-dark-tremor-content";

export default function Config({ profile }) {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [name, setName] = useState(profile?.name || "");
  const [source, setSource] = useState(profile?.source || "");
  const [icon, setIcon] = useState(profile?.image || "");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [blockedDevices, setBlockedDevices] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(profileSchema),
  });

  const onDeleteClick = () => {
    setBlockedDevices(null);
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteOpen(false);
    setBlockedDevices(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await profilesAPI.remove(profile._id);
      navigate("/profiles");
    } catch (error) {
      if (error?.status === 400 && Array.isArray(error.devices)) {
        setBlockedDevices(error.devices);
      } else {
        setDeleteError(error?.error || error?.message || "Failed to delete profile");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const onUpdateClick = async (data) => {
    setLoading(true);
    await profilesAPI.update(profile._id, { name: name, source: source, image: icon });
    setLoading(false);
    navigate("/profiles", { state: { success: "Profile updated successfully" } });
  };

  const getSources = async () => {
    try {
      const response = await sourceAPI.list();
      setSources(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const onNameChange = (val) => {
    setName(val);
    setValue("name", val);
  };

  const onSourceChange = (val) => {
    setSource(val);
    setValue("source", val);
  };

  useEffect(() => {
    getSources();
    setName(profile?.name || "");
    setSource(profile?.source || "");
    setIcon(profile?.image || "");
    setValue("name", profile?.name || "");
    setValue("source", profile?.source || "");
  }, [profile, setValue]);

  useEffect(() => {
    register("name");
    register("source");
  }, [register]);

  return (
    <div>
      <Card>
        <div className="space-y-8">
          <p className={textStyle}>Manage your device profile.</p>
          <form onSubmit={handleSubmit(onUpdateClick)}>
            <div className="form-group mb-6">
              <label
                htmlFor="name"
                className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2"
              >
                Name
                <span className="text-red-700 dark:text-red-400">*</span>
              </label>
              <TextInput
                placeholder="CayenneLLP"
                error={errors.name}
                onValueChange={onNameChange}
                errorMessage={errors.name?.message}
                value={name}
                type="text"
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="provider"
                className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4"
              >
                Pick Source <span className="text-red-700 dark:text-red-400">*</span>
              </label>
              <Select
                name="source"
                onValueChange={onSourceChange}
                value={source}
              >
                {sources.map((source) => (
                  <SelectItem key={source._id} value={source._id}>
                    {source.name}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className="form-group mt-6">
              <label
                htmlFor="icon"
                className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2"
              >
                Icon
              </label>
              <IconPicker value={icon} onChange={setIcon} />
            </div>

            <div className="mt-10">
              <Flex justifyContent="between">
                <Button
                  variant="secondary"
                  color="red"
                  type="button"
                  loading={deleteLoading}
                  onClick={onDeleteClick}
                >
                  Delete
                </Button>

                <Button variant="primary" type="submit" loading={loading}>
                  Update Profile
                </Button>
              </Flex>
            </div>
          </form>
        </div>
      </Card>

      <Dialog
        open={deleteOpen}
        onClose={closeDeleteDialog}
        static={true}
        className="relative z-50"
      >
        <DialogPanel className="max-w-md transform rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl transition-all">
          {blockedDevices ? (
            <>
              <Title>Can't delete this profile</Title>
              <Text className="mt-2">
                <strong>{profile?.name}</strong> is in use by{" "}
                {blockedDevices.length} device
                {blockedDevices.length === 1 ? "" : "s"}. Reassign or delete the
                device{blockedDevices.length === 1 ? "" : "s"} first, then try
                again.
              </Text>
              <ul className="mt-3 list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                {blockedDevices.map((d) => (
                  <li key={d._id} className="text-sm text-gray-700 dark:text-gray-300">
                    {d.name || d._id}
                  </li>
                ))}
              </ul>
              <Flex justifyContent="end" className="mt-4">
                <Button onClick={closeDeleteDialog}>Close</Button>
              </Flex>
            </>
          ) : (
            <>
              <Title>Delete profile?</Title>
              <Text className="mt-2">
                This will permanently delete <strong>{profile?.name}</strong>.
                This cannot be undone.
              </Text>
              {deleteError && (
                <div className="bg-red-100 dark:bg-red-950/40 px-2 py-1 mt-3">
                  <Text className="text-red-600 dark:text-red-300">{deleteError}</Text>
                </div>
              )}
              <Flex justifyContent="end" className="mt-4 space-x-2">
                <Button
                  variant="secondary"
                  onClick={closeDeleteDialog}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  loading={deleteLoading}
                >
                  Delete
                </Button>
              </Flex>
            </>
          )}
        </DialogPanel>
      </Dialog>
    </div>
  );
}
